/**
 * Hook to manage CUA action confirmation requests via SSE bus
 * Reference: TASK-UI-CU-002-1, SOL-UI-CU-002
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useWorkspaceEvents } from '@/app/providers/AgentEventsProvider';
import { respondToConfirmation } from '../api/cuaSessionApi';
import type { CUAConfirmRequest } from '../model/types';

export function useCUAConfirmation() {
  const [pendingRequest, setPendingRequest] = useState<CUAConfirmRequest | null>(null);
  const [timeoutMs, setTimeoutMs] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingRef = useRef<CUAConfirmRequest | null>(null);
  pendingRef.current = pendingRequest;

  useWorkspaceEvents('cua.confirm_request', (event: unknown) => {
    const req = event as CUAConfirmRequest;
    if (req && req.request_id) {
      setPendingRequest(req);
      setTimeoutMs((req.timeout_sec || 10) * 1000);
    }
  });

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const approve = useCallback(async () => {
    const current = pendingRef.current;
    if (!current) return;
    clearTimer();
    setPendingRequest(null);
    try {
      await respondToConfirmation(current.session_id, current.request_id, 'approved');
    } catch (err) {
      console.error('Failed to approve CUA confirmation:', err);
    }
  }, [clearTimer]);

  const reject = useCallback(async () => {
    const current = pendingRef.current;
    if (!current) return;
    clearTimer();
    setPendingRequest(null);
    try {
      await respondToConfirmation(current.session_id, current.request_id, 'rejected');
    } catch (err) {
      console.error('Failed to reject CUA confirmation:', err);
    }
  }, [clearTimer]);

  useEffect(() => {
    if (!pendingRequest) {
      clearTimer();
      return;
    }

    timerRef.current = setInterval(() => {
      setTimeoutMs((prev) => {
        if (prev <= 100) {
          reject();
          return 0;
        }
        return prev - 100;
      });
    }, 100);

    return () => clearTimer();
  }, [pendingRequest, reject, clearTimer]);

  return {
    pendingRequest,
    approve,
    reject,
    timeoutMs,
    setPendingRequest,
    setTimeoutMs,
  };
}
